import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignaturePad } from '../components/SignaturePad';
import { useServiceAreaZips } from '../hooks/useServiceAreaZips';
import { formatDate } from '../utils/dateUtils';
import logo from '../assets/logo_golden_white.png';
import '../styles/KioskMode.css';

interface KioskSession {
  token: string;
  staffId: number;
  expiresAt: string;
}

interface FamilyMember {
  id: string; // Temporary ID for UI
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  raceEthnicity: string;
  relationship: string;
  sameAddress: boolean;
  address: string;
  city: string;
  zipCode: string;
  // Child fields (if applicable)
  schoolName: string;
  grade: string;
  hasAttendanceConcerns: boolean;
  hasDisability: boolean;
}

interface KioskFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  raceEthnicity: string;
  address: string;
  city: string;
  zipCode: string;
  phoneNumber: string;
  emailAddress: string;
  noPhoneAvailable: boolean;
  noEmailAvailable: boolean;
  // Child fields
  schoolName: string;
  grade: string;
  hasAttendanceConcerns: boolean;
  hasDisability: boolean;
  // Signature
  signature: string | null;
  consentGiven: boolean;
  // Family members
  familyMembers: FamilyMember[];
}

const STEPS = [
  { id: 1, label: 'Personal' },
  { id: 2, label: 'Contact' },
  { id: 3, label: 'Family' },
  { id: 4, label: 'Consent' },
  { id: 5, label: 'Review' }
];

const RELATIONSHIP_TYPES = [
  'Spouse/Partner',
  'Child',
  'Parent',
  'Sibling',
  'Grandparent',
  'Grandchild',
  'Other Family Member'
];

export function KioskIntakePage() {
  const navigate = useNavigate();
  const [session, setSession] = useState<KioskSession | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const { isInServiceArea: checkServiceArea } = useServiceAreaZips();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [formData, setFormData] = useState<KioskFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    raceEthnicity: '',
    address: '',
    city: 'Miami',
    zipCode: '',
    phoneNumber: '',
    emailAddress: '',
    noPhoneAvailable: false,
    noEmailAvailable: false,
    schoolName: '',
    grade: '',
    hasAttendanceConcerns: false,
    hasDisability: false,
    signature: null,
    consentGiven: false,
    familyMembers: []
  });
  const [editingMemberIndex, setEditingMemberIndex] = useState<number | null>(null);

  const calculateAge = (dob: string): number => {
    if (!dob) {return 0;}
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(formData.dateOfBirth);
  const isChild = age > 0 && age < 18;
  const isInServiceArea = checkServiceArea(formData.zipCode);

  // Check for valid session on mount
  useEffect(() => {
    const storedSession = sessionStorage.getItem('kiosk_session');
    if (!storedSession) {
      navigate('/kiosk');
      return;
    }

    try {
      const parsed = JSON.parse(storedSession) as KioskSession;
      // Check if session expired
      if (new Date(parsed.expiresAt) < new Date()) {
        sessionStorage.removeItem('kiosk_session');
        navigate('/kiosk');
        return;
      }
      setSession(parsed);
      setSessionChecked(true);
    } catch {
      sessionStorage.removeItem('kiosk_session');
      navigate('/kiosk');
    }
  }, [navigate]);

  // Prevent screen timeout on kiosk
  useEffect(() => {
    if (!sessionChecked) {return;}

    let wakeLock: WakeLockSentinel | null = null;

    const requestWakeLock = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
        } catch (err) {
          console.log('Wake Lock not supported or failed:', err);
        }
      }
    };

    requestWakeLock();

    return () => {
      if (wakeLock) {
        wakeLock.release();
      }
    };
  }, [sessionChecked]);

  // Show loading while checking session
  if (!sessionChecked) {
    return (
      <div className="kiosk-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="kiosk-loading">
          <div className="kiosk-spinner"></div>
          <p style={{ color: 'white', marginTop: '1rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const validateStep = (step: number): boolean => {
    setError('');

    switch (step) {
      case 1:
        if (!formData.firstName.trim()) {
          setError('Please enter your first name');
          return false;
        }
        if (!formData.lastName.trim()) {
          setError('Please enter your last name');
          return false;
        }
        if (!formData.dateOfBirth) {
          setError('Please enter your date of birth');
          return false;
        }
        if (!formData.gender) {
          setError('Please select your gender');
          return false;
        }
        if (!formData.raceEthnicity) {
          setError('Please select your race/ethnicity');
          return false;
        }
        return true;

      case 2:
        if (!formData.address.trim()) {
          setError('Please enter your street address');
          return false;
        }
        if (!formData.city.trim()) {
          setError('Please enter your city');
          return false;
        }
        if (!formData.zipCode || !/^\d{5}$/.test(formData.zipCode)) {
          setError('Please enter a valid 5-digit zip code');
          return false;
        }
        if (!isChild && !formData.noPhoneAvailable && !formData.phoneNumber) {
          setError('Please enter a phone number or check "No phone available"');
          return false;
        }
        return true;

      case 3:
        // Family step - no required fields, skip is allowed
        return true;

      case 4:
        if (!formData.consentGiven) {
          setError('Please check the box to give consent');
          return false;
        }
        if (!formData.signature) {
          setError('Please sign below to confirm');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const addFamilyMember = () => {
    const newMember: FamilyMember = {
      id: `member-${Date.now()}`,
      firstName: '',
      lastName: formData.lastName, // Default to same last name
      dateOfBirth: '',
      gender: '',
      raceEthnicity: '',
      relationship: '',
      sameAddress: true,
      address: formData.address,
      city: formData.city,
      zipCode: formData.zipCode,
      schoolName: '',
      grade: '',
      hasAttendanceConcerns: false,
      hasDisability: false
    };
    setFormData({
      ...formData,
      familyMembers: [...formData.familyMembers, newMember]
    });
    setEditingMemberIndex(formData.familyMembers.length);
  };

  const updateFamilyMember = (index: number, updates: Partial<FamilyMember>) => {
    const updatedMembers = [...formData.familyMembers];
    updatedMembers[index] = { ...updatedMembers[index], ...updates };

    // If sameAddress changed to true, copy primary address
    if (updates.sameAddress === true) {
      updatedMembers[index].address = formData.address;
      updatedMembers[index].city = formData.city;
      updatedMembers[index].zipCode = formData.zipCode;
    }

    setFormData({ ...formData, familyMembers: updatedMembers });
  };

  const removeFamilyMember = (index: number) => {
    const updatedMembers = formData.familyMembers.filter((_, i) => i !== index);
    setFormData({ ...formData, familyMembers: updatedMembers });
    setEditingMemberIndex(null);
  };

  const getMemberAge = (dob: string): number => {
    return calculateAge(dob);
  };

  const isMemberChild = (dob: string): boolean => {
    const memberAge = getMemberAge(dob);
    return memberAge > 0 && memberAge < 18;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setEditingMemberIndex(null); // Close any open member form
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep(currentStep)) {return;}

    setLoading(true);
    setError('');

    try {
      // Prepare family members data
      const familyMembersData = formData.familyMembers.map(member => ({
        firstName: member.firstName.trim(),
        lastName: member.lastName.trim(),
        dateOfBirth: member.dateOfBirth,
        gender: member.gender,
        raceEthnicity: member.raceEthnicity,
        relationship: member.relationship,
        sameAddress: member.sameAddress,
        address: member.sameAddress ? formData.address.trim() : member.address.trim(),
        city: member.sameAddress ? formData.city.trim() : member.city.trim(),
        zipCode: member.sameAddress ? formData.zipCode : member.zipCode,
        // Child fields (if applicable)
        ...(isMemberChild(member.dateOfBirth) && {
          schoolName: member.schoolName || null,
          grade: member.grade || null,
          hasAttendanceConcerns: member.hasAttendanceConcerns,
          hasDisability: member.hasDisability
        })
      }));

      // Prepare submission data
      const submissionData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        raceEthnicity: formData.raceEthnicity,
        address: formData.address.trim(),
        city: formData.city.trim(),
        zipCode: formData.zipCode,
        phoneNumber: formData.noPhoneAvailable ? null : (formData.phoneNumber || null),
        emailAddress: formData.noEmailAvailable ? null : (formData.emailAddress || null),
        intakeDate: new Date().toISOString(),
        clientType: familyMembersData.length > 0 ? 'Family' : 'Individual',
        // Child fields (if applicable)
        ...(isChild && {
          schoolName: formData.schoolName || null,
          grade: formData.grade || null,
          hasAttendanceConcerns: formData.hasAttendanceConcerns,
          hasDisability: formData.hasDisability
        }),
        // Family members
        familyMembers: familyMembersData,
        householdName: familyMembersData.length > 0 ? `${formData.lastName} Household` : null,
        // Kiosk metadata
        submittedViaKiosk: true,
        signature: formData.signature
      };

      // Submit to API with session token
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
      const response = await fetch(`${apiUrl}/kiosk/intake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Kiosk-Token': session?.token || ''
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Failed to submit intake form');
      }

      // Clear session and navigate to success page
      sessionStorage.removeItem('kiosk_session');
      navigate('/kiosk/success', {
        state: {
          clientName: `${formData.firstName} ${formData.lastName}`,
          clientId: result.data?.clientID,
          familyId: result.data?.familyID,
          familyMembers: result.data?.familyMembers,
          householdName: familyMembersData.length > 0 ? `${formData.lastName} Household` : null
        }
      });
    } catch (err: any) {
      console.error('Kiosk submission error:', err);
      setError(err.message || 'Something went wrong. Please ask for assistance.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <h2 className="kiosk-section-title">Personal Information</h2>
            <p className="kiosk-section-description">Please enter your personal details</p>

            <div className="kiosk-form-row">
              <div className="kiosk-form-group">
                <label>First Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Enter first name"
                  autoComplete="given-name"
                />
              </div>

              <div className="kiosk-form-group">
                <label>Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Enter last name"
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="kiosk-form-row">
              <div className="kiosk-form-group">
                <label>Date of Birth <span className="required">*</span></label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                />
                {age > 0 && (
                  <span style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    Age: {age} years old
                  </span>
                )}
              </div>

              <div className="kiosk-form-group">
                <label>Gender <span className="required">*</span></label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                >
                  <option value="">Select gender...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div className="kiosk-form-group">
              <label>Race/Ethnicity <span className="required">*</span></label>
              <select
                value={formData.raceEthnicity}
                onChange={(e) => setFormData({ ...formData, raceEthnicity: e.target.value })}
              >
                <option value="">Select race/ethnicity...</option>
                <option value="Hispanic or Latino">Hispanic or Latino</option>
                <option value="Black or African American">Black or African American</option>
                <option value="White">White</option>
                <option value="Asian">Asian</option>
                <option value="Native Hawaiian or Pacific Islander">Native Hawaiian or Pacific Islander</option>
                <option value="American Indian or Alaska Native">American Indian or Alaska Native</option>
                <option value="Two or more races">Two or more races</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <h2 className="kiosk-section-title">Contact Information</h2>
            <p className="kiosk-section-description">How can we reach you?</p>

            <div className="kiosk-form-group">
              <label>Street Address <span className="required">*</span></label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter street address"
                autoComplete="street-address"
              />
            </div>

            <div className="kiosk-form-row">
              <div className="kiosk-form-group">
                <label>City <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Enter city"
                  autoComplete="address-level2"
                />
              </div>

              <div className="kiosk-form-group">
                <label>Zip Code <span className="required">*</span></label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '') })}
                  placeholder="12345"
                  autoComplete="postal-code"
                />
                {isInServiceArea && (
                  <span style={{ color: '#4CAF50', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    Service area zip code
                  </span>
                )}
              </div>
            </div>

            <div className="kiosk-form-row">
              <div className="kiosk-form-group">
                <label>Phone Number {isChild ? '' : <span className="required">*</span>}</label>
                {formData.noPhoneAvailable ? (
                  <div style={{
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%)',
                    borderRadius: '12px',
                    border: '2px dashed #ccc',
                    color: '#888',
                    textAlign: 'center',
                    fontSize: '1rem',
                    minHeight: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>N/A</span>
                    <span>- No phone available</span>
                  </div>
                ) : (
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/\D/g, '') })}
                    placeholder="3051234567"
                    autoComplete="tel"
                  />
                )}
                <label className="kiosk-checkbox" style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: formData.noPhoneAvailable ? '#FFF8E1' : 'transparent',
                  border: formData.noPhoneAvailable ? '2px solid #FAA31B' : '2px solid transparent',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.noPhoneAvailable}
                    onChange={(e) => setFormData({
                      ...formData,
                      noPhoneAvailable: e.target.checked,
                      phoneNumber: e.target.checked ? '' : formData.phoneNumber
                    })}
                  />
                  <span style={{
                    fontWeight: formData.noPhoneAvailable ? 600 : 400,
                    color: formData.noPhoneAvailable ? '#E65100' : 'inherit'
                  }}>No phone available</span>
                </label>
              </div>

              <div className="kiosk-form-group">
                <label>Email Address</label>
                {formData.noEmailAvailable ? (
                  <div style={{
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%)',
                    borderRadius: '12px',
                    border: '2px dashed #ccc',
                    color: '#888',
                    textAlign: 'center',
                    fontSize: '1rem',
                    minHeight: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>N/A</span>
                    <span>- No email available</span>
                  </div>
                ) : (
                  <input
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                    placeholder="email@example.com"
                    autoComplete="email"
                  />
                )}
                <label className="kiosk-checkbox" style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: formData.noEmailAvailable ? '#FFF8E1' : 'transparent',
                  border: formData.noEmailAvailable ? '2px solid #FAA31B' : '2px solid transparent',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.noEmailAvailable}
                    onChange={(e) => setFormData({
                      ...formData,
                      noEmailAvailable: e.target.checked,
                      emailAddress: e.target.checked ? '' : formData.emailAddress
                    })}
                  />
                  <span style={{
                    fontWeight: formData.noEmailAvailable ? 600 : 400,
                    color: formData.noEmailAvailable ? '#E65100' : 'inherit'
                  }}>No email available</span>
                </label>
              </div>
            </div>

            {isChild && (
              <>
                <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', color: '#5B2C86' }}>
                  Child Information (Optional)
                </h3>

                <div className="kiosk-form-row">
                  <div className="kiosk-form-group">
                    <label>School Name</label>
                    <input
                      type="text"
                      value={formData.schoolName}
                      onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                      placeholder="Enter school name"
                    />
                  </div>

                  <div className="kiosk-form-group">
                    <label>Grade Level</label>
                    <select
                      value={formData.grade}
                      onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    >
                      <option value="">Select grade...</option>
                      <option value="Pre-K">Pre-K</option>
                      <option value="Kindergarten">Kindergarten</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => (
                        <option key={g} value={String(g)}>{g}th Grade</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="kiosk-form-group">
                  <label className="kiosk-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.hasAttendanceConcerns}
                      onChange={(e) => setFormData({ ...formData, hasAttendanceConcerns: e.target.checked })}
                    />
                    <span>Has attendance concerns</span>
                  </label>
                </div>

                <div className="kiosk-form-group">
                  <label className="kiosk-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.hasDisability}
                      onChange={(e) => setFormData({ ...formData, hasDisability: e.target.checked })}
                    />
                    <span>Has documented disability</span>
                  </label>
                </div>
              </>
            )}
          </>
        );

      case 3:
        return (
          <>
            <h2 className="kiosk-section-title">Family Members</h2>
            <p className="kiosk-section-description">
              Add any family members who live with you or will be receiving services (optional)
            </p>

            {formData.familyMembers.length === 0 ? (
              <div style={{
                padding: '2rem',
                background: '#f8f8f8',
                borderRadius: '12px',
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  No family members added yet. You can add spouse/partner, children, or other family members.
                </p>
                <button
                  type="button"
                  className="kiosk-button kiosk-button-primary"
                  onClick={addFamilyMember}
                >
                  + Add Family Member
                </button>
              </div>
            ) : (
              <>
                {/* List of added family members */}
                <div style={{ marginBottom: '1.5rem' }}>
                  {formData.familyMembers.map((member, index) => (
                    <div
                      key={member.id}
                      style={{
                        padding: '1rem',
                        background: editingMemberIndex === index ? '#FFF8E1' : '#f8f8f8',
                        border: editingMemberIndex === index ? '2px solid #FAA31B' : '2px solid transparent',
                        borderRadius: '12px',
                        marginBottom: '0.75rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {editingMemberIndex === index ? (
                        /* Editing form for this member */
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ margin: 0, color: '#5B2C86' }}>
                              {member.relationship || 'New'} Family Member
                            </h4>
                            <button
                              type="button"
                              onClick={() => setEditingMemberIndex(null)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                fontSize: '1.25rem'
                              }}
                            >
                              ✕
                            </button>
                          </div>

                          <div className="kiosk-form-group">
                            <label>Relationship <span className="required">*</span></label>
                            <select
                              value={member.relationship}
                              onChange={(e) => updateFamilyMember(index, { relationship: e.target.value })}
                            >
                              <option value="">Select relationship...</option>
                              {RELATIONSHIP_TYPES.map(rel => (
                                <option key={rel} value={rel}>{rel}</option>
                              ))}
                            </select>
                          </div>

                          <div className="kiosk-form-row">
                            <div className="kiosk-form-group">
                              <label>First Name <span className="required">*</span></label>
                              <input
                                type="text"
                                value={member.firstName}
                                onChange={(e) => updateFamilyMember(index, { firstName: e.target.value })}
                                placeholder="Enter first name"
                              />
                            </div>
                            <div className="kiosk-form-group">
                              <label>Last Name <span className="required">*</span></label>
                              <input
                                type="text"
                                value={member.lastName}
                                onChange={(e) => updateFamilyMember(index, { lastName: e.target.value })}
                                placeholder="Enter last name"
                              />
                            </div>
                          </div>

                          <div className="kiosk-form-row">
                            <div className="kiosk-form-group">
                              <label>Date of Birth <span className="required">*</span></label>
                              <input
                                type="date"
                                value={member.dateOfBirth}
                                onChange={(e) => updateFamilyMember(index, { dateOfBirth: e.target.value })}
                                max={new Date().toISOString().split('T')[0]}
                              />
                              {member.dateOfBirth && getMemberAge(member.dateOfBirth) > 0 && (
                                <span style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                  Age: {getMemberAge(member.dateOfBirth)} years old
                                </span>
                              )}
                            </div>
                            <div className="kiosk-form-group">
                              <label>Gender <span className="required">*</span></label>
                              <select
                                value={member.gender}
                                onChange={(e) => updateFamilyMember(index, { gender: e.target.value })}
                              >
                                <option value="">Select gender...</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Non-binary">Non-binary</option>
                                <option value="Prefer not to say">Prefer not to say</option>
                              </select>
                            </div>
                          </div>

                          <div className="kiosk-form-group">
                            <label>Race/Ethnicity <span className="required">*</span></label>
                            <select
                              value={member.raceEthnicity}
                              onChange={(e) => updateFamilyMember(index, { raceEthnicity: e.target.value })}
                            >
                              <option value="">Select race/ethnicity...</option>
                              <option value="Hispanic or Latino">Hispanic or Latino</option>
                              <option value="Black or African American">Black or African American</option>
                              <option value="White">White</option>
                              <option value="Asian">Asian</option>
                              <option value="Native Hawaiian or Pacific Islander">Native Hawaiian or Pacific Islander</option>
                              <option value="American Indian or Alaska Native">American Indian or Alaska Native</option>
                              <option value="Two or more races">Two or more races</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>

                          <div className="kiosk-form-group">
                            <label className="kiosk-checkbox">
                              <input
                                type="checkbox"
                                checked={member.sameAddress}
                                onChange={(e) => updateFamilyMember(index, { sameAddress: e.target.checked })}
                              />
                              <span>Lives at the same address</span>
                            </label>
                          </div>

                          {!member.sameAddress && (
                            <>
                              <div style={{
                                padding: '0.75rem 1rem',
                                background: '#FFF3E0',
                                borderRadius: '8px',
                                marginBottom: '1rem',
                                fontSize: '0.9rem',
                                color: '#E65100'
                              }}>
                                Address is optional - leave blank if unknown
                              </div>
                              <div className="kiosk-form-group">
                                <label>Street Address <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                                <input
                                  type="text"
                                  value={member.address}
                                  onChange={(e) => updateFamilyMember(index, { address: e.target.value })}
                                  placeholder="Enter street address if known"
                                />
                              </div>
                              <div className="kiosk-form-row">
                                <div className="kiosk-form-group">
                                  <label>City <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                                  <input
                                    type="text"
                                    value={member.city}
                                    onChange={(e) => updateFamilyMember(index, { city: e.target.value })}
                                    placeholder="Enter city"
                                  />
                                </div>
                                <div className="kiosk-form-group">
                                  <label>Zip Code <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={5}
                                    value={member.zipCode}
                                    onChange={(e) => updateFamilyMember(index, { zipCode: e.target.value.replace(/\D/g, '') })}
                                    placeholder="12345"
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {/* Child-specific fields */}
                          {isMemberChild(member.dateOfBirth) && (
                            <>
                              <h4 style={{ marginTop: '1rem', marginBottom: '0.75rem', color: '#5B2C86' }}>
                                School Information {!member.sameAddress && <span style={{ color: '#888', fontWeight: 'normal', fontSize: '0.9rem' }}>(optional - leave blank if unknown)</span>}
                              </h4>
                              {!member.sameAddress && (
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#E3F2FD',
                                  borderRadius: '6px',
                                  marginBottom: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: '#1565C0'
                                }}>
                                  School info is optional if the child lives elsewhere
                                </div>
                              )}
                              <div className="kiosk-form-row">
                                <div className="kiosk-form-group">
                                  <label>School Name {!member.sameAddress && <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>}</label>
                                  <input
                                    type="text"
                                    value={member.schoolName}
                                    onChange={(e) => updateFamilyMember(index, { schoolName: e.target.value })}
                                    placeholder={member.sameAddress ? "Enter school name" : "Enter if known"}
                                  />
                                </div>
                                <div className="kiosk-form-group">
                                  <label>Grade Level {!member.sameAddress && <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>}</label>
                                  <select
                                    value={member.grade}
                                    onChange={(e) => updateFamilyMember(index, { grade: e.target.value })}
                                  >
                                    <option value="">Select grade...</option>
                                    <option value="Pre-K">Pre-K</option>
                                    <option value="Kindergarten">Kindergarten</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => (
                                      <option key={g} value={String(g)}>{g}th Grade</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="kiosk-form-group">
                                <label className="kiosk-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={member.hasAttendanceConcerns}
                                    onChange={(e) => updateFamilyMember(index, { hasAttendanceConcerns: e.target.checked })}
                                  />
                                  <span>Has attendance concerns</span>
                                </label>
                              </div>
                              <div className="kiosk-form-group">
                                <label className="kiosk-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={member.hasDisability}
                                    onChange={(e) => updateFamilyMember(index, { hasDisability: e.target.checked })}
                                  />
                                  <span>Has documented disability</span>
                                </label>
                              </div>
                            </>
                          )}

                          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                            <button
                              type="button"
                              className="kiosk-button kiosk-button-secondary"
                              onClick={() => removeFamilyMember(index)}
                              style={{ flex: 1, background: '#ffebee', color: '#c62828', borderColor: '#ef9a9a' }}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              className="kiosk-button kiosk-button-primary"
                              onClick={() => setEditingMemberIndex(null)}
                              style={{ flex: 1 }}
                              disabled={!member.firstName || !member.lastName || !member.dateOfBirth || !member.gender || !member.raceEthnicity || !member.relationship}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Collapsed view of this member */
                        <div
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                          onClick={() => setEditingMemberIndex(index)}
                        >
                          <div>
                            <strong style={{ color: '#5B2C86' }}>
                              {member.firstName} {member.lastName}
                            </strong>
                            <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                              ({member.relationship || 'Relationship not set'})
                            </span>
                            {member.dateOfBirth && (
                              <span style={{ color: '#888', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                                - {getMemberAge(member.dateOfBirth)} yrs
                              </span>
                            )}
                            {member.sameAddress && (
                              <span style={{
                                marginLeft: '0.75rem',
                                padding: '0.125rem 0.5rem',
                                background: '#E8F5E9',
                                color: '#2E7D32',
                                borderRadius: '4px',
                                fontSize: '0.75rem'
                              }}>
                                Same address
                              </span>
                            )}
                          </div>
                          <span style={{ color: '#5B2C86', fontSize: '1.25rem' }}>✎</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="kiosk-button kiosk-button-secondary"
                  onClick={addFamilyMember}
                  style={{ width: '100%' }}
                >
                  + Add Another Family Member
                </button>
              </>
            )}

            {formData.familyMembers.length > 0 && (
              <div className="kiosk-alert kiosk-alert-info" style={{ marginTop: '1.5rem' }}>
                <strong>Household Name:</strong> {formData.lastName} Household
                <br />
                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  {formData.familyMembers.length + 1} member{formData.familyMembers.length > 0 ? 's' : ''} total (including you)
                </span>
              </div>
            )}
          </>
        );

      case 4:
        return (
          <>
            <h2 className="kiosk-section-title">Consent & Signature</h2>
            <p className="kiosk-section-description">Please review and sign below</p>

            <div className="kiosk-alert kiosk-alert-info">
              By signing below, I consent to Belafonte TACOLCY Center collecting and using
              my information{formData.familyMembers.length > 0 ? ' and my family members\' information' : ''} to provide services.
              I understand that this information will be kept confidential and used only for program purposes.
            </div>

            <div className="kiosk-form-group">
              <label className="kiosk-checkbox" style={{
                padding: '1rem 1.25rem',
                background: formData.consentGiven
                  ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)'
                  : 'transparent',
                border: formData.consentGiven
                  ? '2px solid #4CAF50'
                  : '2px solid #e0e0e0',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.consentGiven}
                  onChange={(e) => setFormData({ ...formData, consentGiven: e.target.checked })}
                />
                <span style={{
                  fontWeight: formData.consentGiven ? 600 : 400,
                  color: formData.consentGiven ? '#2E7D32' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {formData.consentGiven && (
                    <span style={{ fontSize: '1.25rem' }}>✓</span>
                  )}
                  I have read and agree to the terms above <span className="required">*</span>
                </span>
              </label>
              {formData.consentGiven && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: '#E8F5E9',
                  borderRadius: '8px',
                  color: '#2E7D32',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.1rem' }}>✓</span>
                  Consent acknowledged - please sign below
                </div>
              )}
            </div>

            <div className="kiosk-form-group">
              <label>Signature <span className="required">*</span></label>
              <SignaturePad
                onSignatureChange={(sig) => setFormData({ ...formData, signature: sig })}
                width={Math.min(window.innerWidth - 100, 500)}
                height={150}
              />
            </div>
          </>
        );

      case 5:
        return (
          <>
            <h2 className="kiosk-section-title">Review Your Information</h2>
            <p className="kiosk-section-description">Please verify all information is correct</p>

            <div style={{
              background: '#f8f8f8',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#5B2C86' }}>Your Personal Information</h3>
              <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
              <p><strong>Date of Birth:</strong> {formatDate(formData.dateOfBirth)} ({age} years old)</p>
              <p><strong>Gender:</strong> {formData.gender}</p>
              <p><strong>Race/Ethnicity:</strong> {formData.raceEthnicity}</p>
            </div>

            <div style={{
              background: '#f8f8f8',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#5B2C86' }}>Contact Information</h3>
              <p><strong>Address:</strong> {formData.address}</p>
              <p><strong>City, Zip:</strong> {formData.city}, {formData.zipCode}</p>
              <p><strong>Phone:</strong> {formData.noPhoneAvailable ? 'Not provided' : (formData.phoneNumber || 'Not provided')}</p>
              <p><strong>Email:</strong> {formData.noEmailAvailable ? 'Not provided' : (formData.emailAddress || 'Not provided')}</p>
            </div>

            {isChild && (formData.schoolName || formData.grade) && (
              <div style={{
                background: '#f8f8f8',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#5B2C86' }}>Child Information</h3>
                {formData.schoolName && <p><strong>School:</strong> {formData.schoolName}</p>}
                {formData.grade && <p><strong>Grade:</strong> {formData.grade}</p>}
                {formData.hasAttendanceConcerns && <p>Has attendance concerns</p>}
                {formData.hasDisability && <p>Has documented disability</p>}
              </div>
            )}

            {/* Family Members Section */}
            {formData.familyMembers.length > 0 && (
              <div style={{
                background: '#FFF8E1',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1rem',
                border: '2px solid #FAA31B'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#5B2C86' }}>
                  {formData.lastName} Household ({formData.familyMembers.length + 1} members)
                </h3>
                {formData.familyMembers.map((member, index) => (
                  <div key={member.id} style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: index < formData.familyMembers.length - 1 ? '0.75rem' : 0
                  }}>
                    <p style={{ margin: '0 0 0.5rem 0' }}>
                      <strong>{member.firstName} {member.lastName}</strong>
                      <span style={{ color: '#666', marginLeft: '0.5rem' }}>({member.relationship})</span>
                    </p>
                    <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', color: '#666' }}>
                      {formatDate(member.dateOfBirth)} ({getMemberAge(member.dateOfBirth)} yrs) • {member.gender} • {member.raceEthnicity}
                    </p>
                    {member.sameAddress ? (
                      <p style={{ margin: '0', fontSize: '0.85rem', color: '#2E7D32' }}>
                        Same address as primary
                      </p>
                    ) : member.address ? (
                      <p style={{ margin: '0', fontSize: '0.85rem', color: '#666' }}>
                        {member.address}{member.city && `, ${member.city}`}{member.zipCode && `, ${member.zipCode}`}
                      </p>
                    ) : (
                      <p style={{ margin: '0', fontSize: '0.85rem', color: '#888', fontStyle: 'italic' }}>
                        Different address (not provided)
                      </p>
                    )}
                    {isMemberChild(member.dateOfBirth) && (member.schoolName || member.grade) && (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                        {member.schoolName && `School: ${member.schoolName}`}
                        {member.schoolName && member.grade && ' • '}
                        {member.grade && `Grade: ${member.grade}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="kiosk-alert kiosk-alert-success">
              Consent given and signature captured
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="kiosk-page">
      <div className="kiosk-container">
        <header className="kiosk-header">
          <img src={logo} alt="Belafonte TACOLCY Center" />
          <h1>Client Intake Form</h1>
          <p>Welcome! Please complete this form to register for our services.</p>
        </header>

        {/* Progress indicator */}
        <div className="kiosk-progress">
          {STEPS.map((step) => (
            <div
              key={step.id}
              className={`kiosk-progress-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
            >
              <div className="kiosk-progress-dot">
                {currentStep > step.id ? '✓' : step.id}
              </div>
              <span className="kiosk-progress-label">{step.label}</span>
            </div>
          ))}
        </div>

        <form className="kiosk-form-card" onSubmit={handleSubmit}>
          {error && (
            <div className="kiosk-alert kiosk-alert-error">
              {error}
            </div>
          )}

          {renderStep()}

          <div className="kiosk-nav-buttons">
            {currentStep > 1 && (
              <button
                type="button"
                className="kiosk-button kiosk-button-secondary"
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </button>
            )}

            {currentStep < 5 ? (
              <button
                type="button"
                className="kiosk-button kiosk-button-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="kiosk-button kiosk-button-gold"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="kiosk-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Help button */}
      <button
        type="button"
        className="kiosk-help-button"
        onClick={() => setShowHelp(true)}
        aria-label="Get help"
      >
        ?
      </button>

      {/* Help modal */}
      {showHelp && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: '#5B2C86', marginTop: 0 }}>Need Help?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '1.5rem' }}>
              Please ask a staff member for assistance.
            </p>
            <button
              className="kiosk-button kiosk-button-primary"
              onClick={() => setShowHelp(false)}
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
